import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { useDebouncedEffect } from "../../../../packages/hooks";
import { helpers } from "../../../../packages/helpers";
import { isChatOpen, useControlContext } from "../../../control";
import { useNetworkContext } from "../../../network";
import {
  getMessages,
  getPrevMessages,
  getNextMessages,
  increaseUnreadCount,
} from "../../actions";
import {
  selectChatId,
  selectLastMessageRole,
  selectLastMessage,
  selectChatMessages,
  selectTopAndBottomMessageIds,
  selectUnreadMessagesCount,
} from "../../selectors";
import { useReplyPathContext } from "../../contexts";
import {
  DIRECTIONS,
  NEAR_LINE_HEIGHT,
  DEFAULT_SCROLL_STATE,
} from "./constants";
import { isDownDirection, isUpDirection } from "./predicates";
import { useMessagesNew } from "./use-message-new";
import { isAllowedDownload } from "./helpers";

const {
  isAuthorUser,
  isAuthorOpponent,
  isMessageReceived,
  isMessageChannelSSE,
} = helpers.predicates;

export const useMessageList = () => {
  const dispatch = useAppDispatch();
  const { api, chatState } = useControlContext();

  const { handleResetReplyMessage } = useReplyPathContext();

  const { firstUnreadMessageId, handleShowServiceMessageNew } =
    useMessagesNew();

  const {
    isGetMessagesPending,
    isPrevMessagesPending,
    isNextMessagesPending,
    isPrevMessagesLoading,
    isGetMessagesSuccess,
  } = useNetworkContext();

  const unreadCount = useAppSelector(selectUnreadMessagesCount);
  const chatId = useAppSelector(selectChatId);
  const messages = useAppSelector(selectChatMessages);
  const lastMessageUserRole = useAppSelector(selectLastMessageRole);

  const { topMessageId, bottomMessageId } = useAppSelector(
    selectTopAndBottomMessageIds
  );

  const { status: lastMessageStatus, channel: lastMessageChannel } =
    useAppSelector(selectLastMessage);

  const [{ position, direction, isNearTop, isNearBottom }, setScrolling] =
    useState(DEFAULT_SCROLL_STATE);

  const { getMessagesApi } = api;

  const scrollRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef(NaN);
  const heightRef = useRef(NaN);

  const messagesLength = messages.length;
  const isUserSendMessage =
    isAuthorUser(lastMessageUserRole) && !bottomMessageId;

  const downloadPrevMessages = () =>
    dispatch(
      getPrevMessages({
        getMessagesApi,
        chatId,
        messageId: topMessageId,
      })
    );

  const downloadNextMessages = () =>
    dispatch(
      getNextMessages({
        getMessagesApi,
        chatId,
        messageId: bottomMessageId,
      })
    );

  const downloadCallback = () => {
    if (!isAllowedDownload(messagesLength)) {
      return;
    }

    if (isPrevMessagesPending && isNearTop && isUpDirection(direction)) {
      downloadPrevMessages();

      return;
    }

    if (isNextMessagesPending && isNearBottom && isDownDirection(direction)) {
      downloadNextMessages();

      return;
    }
  };

  const handleScrollDownClick = () => {
    if (isGetMessagesPending) {
      const params = { getMessagesApi, chatId };

      dispatch(getMessages(params));
    } else {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const {
      scrollTop: position,
      scrollHeight: height,
      clientHeight,
    } = event.currentTarget;

    const positionBottom = height - position - clientHeight;
    const isNearTop = position < NEAR_LINE_HEIGHT;
    const isNearBottom = positionBottom < NEAR_LINE_HEIGHT;

    const direction =
      positionRef.current < position ? DIRECTIONS.DOWN : DIRECTIONS.UP;

    const scrollExtraParams = {
      direction,
      position,
      isNearTop,
      isNearBottom,
    };

    if (!positionBottom) {
      handleResetReplyMessage();
    }

    setScrolling(scrollExtraParams);
    positionRef.current = position;
  };

  const resetScroll = () =>
    setScrolling((prev) => ({
      ...prev,
      position: 0,
      direction: DIRECTIONS.NONE,
    }));

  useEffect(() => {
    if (isNearTop || isNearBottom) {
      downloadCallback();
    }
  }, [isNearTop, isNearBottom]);

  useEffect(() => {
    // Мгновенно переносит скролл вниз к последнему сообщению при загрузке сообщений
    if (isGetMessagesSuccess) {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "auto",
      });

      setScrolling(DEFAULT_SCROLL_STATE);
    }
  }, [isGetMessagesSuccess]);

  useEffect(() => {
    const isNewMessageSentByUser = isUserSendMessage;
    const isNewMessageFromSSE =
      isNearBottom && isMessageChannelSSE(lastMessageChannel);

    // делает прокрутку после появления сообщения в хранилище
    // timeout необходим, так как обработка файлового сообщения занимает время
    if (isNewMessageFromSSE || isNewMessageSentByUser) {
      setTimeout(handleScrollDownClick, 200);
    }
  }, [messagesLength]);

  useEffect(() => {
    // Сохраняем прошлую высоту скролла
    if (isPrevMessagesLoading && scrollRef.current) {
      heightRef.current = scrollRef.current.scrollHeight;
    }

    // Переносим скролл на прошлую точку после подгрузки сообщений
    const prevHeight = heightRef.current;
    const newHeight = scrollRef?.current?.scrollHeight;

    if (!isPrevMessagesLoading && prevHeight && newHeight) {
      scrollRef.current.scrollTo(0, newHeight - prevHeight + NEAR_LINE_HEIGHT);

      heightRef.current = NaN;
    }
  }, [isPrevMessagesLoading]);

  useEffect(() => {
    const isOpponentMessage = isAuthorOpponent(lastMessageUserRole);
    const isReceivedMessage = isMessageReceived(lastMessageStatus);
    const isMessageFromSSE = isMessageReceived(lastMessageStatus);

    // Если пользователь прокрутил список сообщений вверх и появились непрочитанные от оппонентов
    if (
      !isNearBottom &&
      isOpponentMessage &&
      isReceivedMessage &&
      isMessageFromSSE
    ) {
      // Увеличиваем счетчик непрочитанных
      dispatch(increaseUnreadCount());

      // включаем сервисное сообщение
      handleShowServiceMessageNew();
    }
  }, [bottomMessageId]);

  useEffect(() => {
    // если чат открылся и загрузились сообщения и есть непрочитанные от оппонентов
    if (isChatOpen(chatState) && isGetMessagesSuccess && unreadCount) {
      // включаем сервисное сообщение
      handleShowServiceMessageNew();
    }
  }, [chatState, isGetMessagesSuccess]);

  useDebouncedEffect({
    // сбрасывает direction и scroll при остановке прокнутки
    effect: resetScroll,
    deps: [position],
    delay: 2000,
  });

  return {
    scrollRef,
    unreadCount,
    firstUnreadMessageId,

    isScrolling: !!position,
    handleScroll,
    handleScrollDownClick,

    shouldShownScrollDownButton: !isNearBottom,
  };
};
